import React, { useEffect, useMemo, useState } from 'react';
import QRCode from 'qrcode';
import { MessageCircle, Download, UserCircle2, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { trackPreviewEvent } from '@/lib/analytics';

// "Invite Your Doctor" QR — patient-side acquisition tool.
// Encodes a link to /brochure?via=patient (+ optional patient name).
// The patient shows the QR to their doctor in person at the next visit; the
// doctor scans, lands on DocSite's brochure, and signs up. Zero SMS cost,
// physical-world friendly, and complements the WhatsApp share flow.
//
// Usage:
//   <InviteYourDoctorQR />                              // default name from props
//   <InviteYourDoctorQR defaultName="Priya" />          // pre-filled
//   <InviteYourDoctorQR variant="compact" />            // smaller card for sidebars
export default function InviteYourDoctorQR({
  defaultName = '',
  variant = 'hero',          // 'hero' | 'compact'
  className = '',
}) {
  const [name, setName] = useState(defaultName);
  const [dataUrl, setDataUrl] = useState('');

  const encodedUrl = useMemo(() => {
    const trimmed = (name || '').trim();
    const base = 'https://docsite.care/brochure?via=patient';
    return trimmed ? `${base}&p=${encodeURIComponent(trimmed)}` : base;
  }, [name]);

  useEffect(() => {
    let cancelled = false;
    QRCode.toDataURL(encodedUrl, {
      width: 600,
      margin: 2,
      errorCorrectionLevel: 'Q',
      color: { dark: '#0f172a', light: '#ffffff' },
    })
      .then((d) => { if (!cancelled) setDataUrl(d); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [encodedUrl]);

  const downloadQR = () => {
    if (!dataUrl) return;
    trackPreviewEvent('invite_qr_download');
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = `docsite-invite-${(name || 'patient').toLowerCase().replace(/\s+/g, '-')}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  if (variant === 'compact') {
    return (
      <div className={`rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-sky-50 p-4 ${className}`} data-testid="invite-doctor-qr-compact">
        <div className="flex items-center gap-3">
          <div className="w-20 h-20 rounded-lg bg-white p-1.5 border border-slate-200 flex-shrink-0">
            {dataUrl
              ? <img src={dataUrl} alt="Invite your doctor QR" className="w-full h-full object-contain" data-testid="invite-doctor-qr-image" />
              : <div className="w-full h-full bg-slate-100 rounded animate-pulse" />}
          </div>
          <div className="min-w-0">
            <p className="text-[10px] font-bold text-emerald-700 uppercase tracking-[0.18em]">Invite Your Doctor</p>
            <p className="text-sm font-semibold text-slate-900 leading-tight mt-0.5" style={{ fontFamily: 'Outfit, sans-serif' }}>Show this at your next visit</p>
            <p className="text-[11px] text-slate-500 leading-snug mt-0.5">Doctor scans → opens brochure</p>
          </div>
        </div>
      </div>
    );
  }

  // Hero variant — fills a card on /my-clinics, /invite-your-doctor.
  return (
    <div className={`rounded-3xl border border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-sky-50/60 p-6 md:p-8 shadow-sm ${className}`} data-testid="invite-doctor-qr-hero">
      <div className="grid md:grid-cols-[auto_1fr] gap-6 items-center">
        {/* QR */}
        <div className="flex flex-col items-center gap-3">
          <div className="w-44 h-44 md:w-48 md:h-48 rounded-2xl bg-white p-3 border border-slate-200 shadow-sm">
            {dataUrl
              ? <img src={dataUrl} alt="Invite your doctor QR" className="w-full h-full object-contain" data-testid="invite-doctor-qr-image" />
              : <div className="w-full h-full bg-slate-100 rounded animate-pulse" />}
          </div>
          <button
            onClick={downloadQR}
            type="button"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-slate-600 text-[11px] font-semibold hover:border-slate-300 transition-colors"
            data-testid="invite-doctor-qr-download"
          >
            <Download size={12} /> Save QR as PNG
          </button>
        </div>

        {/* Copy + name + actions */}
        <div className="min-w-0">
          <p className="text-[10px] font-bold text-emerald-700 uppercase tracking-[0.2em] mb-2 flex items-center gap-1.5">
            <MessageCircle size={11} /> Bring a doctor onto DocSite
          </p>
          <h3 className="text-xl md:text-2xl font-medium text-slate-900 leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Show this QR to your doctor at your next visit.
          </h3>
          <p className="text-sm text-slate-600 mt-2 leading-relaxed">
            Your doctor scans it from your phone screen — DocSite&apos;s brochure opens on theirs. They can sign up free in 30 seconds. No SMS sent, no calls — just a friendly nudge in person.
          </p>

          <label htmlFor="invite-qr-name" className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em] mt-5 mb-2 flex items-center gap-1.5">
            <UserCircle2 size={11} /> Add your name (optional, makes the link personal)
          </label>
          <input
            id="invite-qr-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={40}
            placeholder="e.g. Priya"
            className="w-full sm:w-64 px-3.5 py-2 rounded-xl border border-slate-200 bg-white text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100 transition-colors"
            data-testid="invite-doctor-qr-name"
          />

          <div className="mt-4 flex flex-wrap gap-2">
            <Link
              to="/invite-your-doctor"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-emerald-500 text-white text-xs font-semibold hover:bg-emerald-600 transition-colors"
              data-testid="invite-doctor-qr-whatsapp-link"
            >
              <MessageCircle size={12} /> Or send on WhatsApp
            </Link>
            <a
              href={encodedUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-slate-200 text-slate-700 text-xs font-semibold hover:border-sky-300 hover:text-sky-700 transition-colors"
              data-testid="invite-doctor-qr-preview-link"
            >
              <ExternalLink size={12} /> Preview the brochure
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
